import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { LoadingSpinner, IconTrophy, IconFlame, IconMapPin, IconMessageCircle } from '../components/ui/Icons';
import { API_URL } from '../utils/api';
import BackButton from '../components/ui/BackButton';

const PublicProfile = () => {
    const { profileSlug } = useParams();
    const navigate = useNavigate();
    const { user, fetchWithAuth } = useContext(AuthContext);

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPublicProfile = async () => {
            try {
                // Ensure we handle the @ prefix if it's in the URL param but not stripped by router
                // (Though usually router param extracts the value. We defined path as /profile/@:slug)
                // If url is /profile/@dummy01, slug is dummy01.

                const actualSlug = profileSlug.startsWith('@') ? profileSlug.substring(1) : profileSlug;

                const res = await fetch(`${API_URL}/api/public/profiles/${actualSlug}/`, {
                    headers: {
                        'Content-Type': 'application/json',
                        // Include auth header if user is logged in (optional, but good for privacy checks on backend if needed later)
                        ...(user && localStorage.getItem('authToken') ? { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } : {})
                    }
                });

                if (res.ok) {
                    const data = await res.json();
                    setProfile(data);
                } else {
                    if (res.status === 404) {
                        setError('Profil nicht gefunden oder privat.');
                    } else {
                        setError('Ein Fehler ist aufgetreten.');
                    }
                }
            } catch (err) {
                console.error(err);
                setError('Verbindungsfehler.');
            } finally {
                setLoading(false);
            }
        };

        if (profileSlug) {
            fetchPublicProfile();
        }
    }, [profileSlug, user]);

    if (loading) return <LoadingSpinner />;

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] px-4 text-center">
                <div className="text-6xl mb-4">🙈</div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Hoppla!</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-plantlix-600 hover:bg-plantlix-500 text-white rounded-full font-bold transition-colors"
                >
                    Zurück zum Start
                </button>
            </div>
        );
    }

    if (!profile) return null;

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <BackButton />

            <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-800">

                {/* Header / Banner area */}
                <div className="h-32 bg-gradient-to-r from-plantlix-600 to-plantlix-600 relative">
                    <div className="absolute -bottom-12 left-8 p-1 bg-white dark:bg-[#1a1a1a] rounded-full">
                        <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-800 border-4 border-white dark:border-[#1a1a1a]">
                            {profile.avatar_url ? (
                                <img src={profile.avatar_url} alt={profile.username} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-gray-400 bg-gray-100 dark:bg-gray-800">
                                    {profile.username?.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="pt-16 pb-8 px-8">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">

                        {/* User Info */}
                        <div className="flex-1">
                            <h1 className="text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                                {profile.username}
                                {profile.is_staff && (
                                    <span className="bg-blue-500/10 text-blue-500 text-xs px-2 py-0.5 rounded-full border border-blue-500/20 font-bold uppercase tracking-wider">
                                        Team
                                    </span>
                                )}
                            </h1>

                            {profile.location && (
                                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mt-1">
                                    <IconMapPin className="w-4 h-4" />
                                    <span className="text-sm font-medium">{profile.location}</span>
                                </div>
                            )}

                            {profile.bio && (
                                <p className="mt-4 text-gray-700 dark:text-gray-300 max-w-2xl leading-relaxed whitespace-pre-wrap">
                                    {profile.bio}
                                </p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-3 min-w-[200px]">
                            {/* Sniggle Button (if allowed) */}
                            {user && user.username !== profile.username && (
                                <button
                                    onClick={() => navigate(`/sniggle/messages/${profile.user_id}`)}
                                    disabled={!profile.allow_sniggles} // TODO: Handle this logic properly based on friendship or public setting
                                    className="flex items-center justify-center gap-2 w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-500/20 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <IconMessageCircle className="w-5 h-5" />
                                    <span>Nachricht senden</span>
                                </button>
                            )}

                            {/* Add Friend (Placeholder logic) */}
                            {user && user.username !== profile.username && (
                                <button
                                    disabled
                                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-100 dark:bg-gray-800 text-gray-400 rounded-xl font-bold cursor-not-allowed"
                                >
                                    <span>Freund hinzufügen</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Stats Grid */}
                    {profile.show_stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-gray-100 dark:border-gray-800">
                            <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                                <div className="flex items-center gap-2 text-gray-500 mb-1">
                                    <IconTrophy className="w-4 h-4 text-yellow-500" />
                                    <span className="text-xs font-bold uppercase tracking-wider">Level</span>
                                </div>
                                <div className="text-2xl font-black text-gray-900 dark:text-white">
                                    {profile.level_info?.level || 1}
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                                <div className="flex items-center gap-2 text-gray-500 mb-1">
                                    <IconFlame className="w-4 h-4 text-orange-500" />
                                    <span className="text-xs font-bold uppercase tracking-wider">Streak</span>
                                </div>
                                <div className="text-2xl font-black text-gray-900 dark:text-white">
                                    {profile.streak_days || 0} <span className="text-sm font-normal text-gray-400">Tage</span>
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                                <div className="flex items-center gap-2 text-gray-500 mb-1">
                                    <div className="w-4 h-4 rounded-full bg-plantlix-500" />
                                    <span className="text-xs font-bold uppercase tracking-wider">Gelernt</span>
                                </div>
                                <div className="text-2xl font-black text-gray-900 dark:text-white">
                                    {profile.stats?.learned_count || 0}
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                                <div className="flex items-center gap-2 text-gray-500 mb-1">
                                    <span className="text-xs font-bold uppercase tracking-wider">Beigetreten</span>
                                </div>
                                <div className="text-lg font-bold text-gray-900 dark:text-white">
                                    {new Date(profile.date_joined).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicProfile;
